import { useEffect, useMemo, useRef, useState } from 'react';
import type { Idea, Tag, User } from '../api';
import * as api from '../api';
import { isIdeaLimitError } from '../api';
import type { View } from './Sidebar';
import { Icon, type IconName } from '../icons';
import { useTheme } from '../hooks/useTheme';
import { useFontSize } from '../hooks/useFontSize';

// The ⌘K box (P3-06, D-78): one dialog for capture and search.
//   search   (⌘K, Search) — ideas by meaning and words, matching tags, "/" commands;
//            ↵ opens the highlighted row, ⇧↵ saves the query as a new idea.
//   capture  (New idea, C, share target) — title, optional details, #tags; similar
//            ideas already in the library show before saving; ↵ saves, ⌘↵ saves and opens.

export type QuickMode = 'search' | 'capture';

const STATUS_LABEL: Record<string, string> = {
  captured: 'Captured', exploring: 'Exploring', building: 'Building', shipped: 'Shipped', shelved: 'Shelved',
};

type Action = 'dark' | 'light' | 'system' | 'bigger' | 'smaller';

const COMMANDS: { id: View | Action; label: string; icon: IconName; words: string }[] = [
  { id: 'ideas', label: 'Go to Ideas', icon: 'ideas', words: 'ideas list library' },
  { id: 'board', label: 'Go to Board', icon: 'board', words: 'board kanban' },
  { id: 'map', label: 'Go to Mind map', icon: 'map', words: 'map mind graph' },
  { id: 'timeline', label: 'Go to Timeline', icon: 'timeline', words: 'timeline history shipped latency' },
  { id: 'review', label: 'Go to Triage', icon: 'triage', words: 'triage review weekly' },
  { id: 'duplicates', label: 'Go to Duplicates', icon: 'dupes', words: 'duplicates dupes merge' },
  { id: 'archive', label: 'Go to Archive', icon: 'archive', words: 'archive archived' },
  { id: 'settings', label: 'Open Settings', icon: 'settings', words: 'settings preferences account billing' },
  // Theme and text size from the keyboard (D-84 S2)
  { id: 'dark', label: 'Theme: dark', icon: 'moon', words: 'theme dark appearance' },
  { id: 'light', label: 'Theme: light', icon: 'sun', words: 'theme light appearance' },
  { id: 'system', label: 'Theme: match system', icon: 'auto', words: 'theme system auto appearance' },
  { id: 'bigger', label: 'Text size: bigger', icon: 'add', words: 'text size bigger larger font zoom' },
  { id: 'smaller', label: 'Text size: smaller', icon: 'filter', words: 'text size smaller font zoom' },
];

const VIEWS = new Set<string>(['ideas', 'board', 'map', 'timeline', 'review', 'duplicates', 'archive', 'settings']);

/** "#tag" words in a title become tags; the rest stays the title. */
export function splitHashTags(text: string): { title: string; tags: string[] } {
  const tags: string[] = [];
  const title = text
    .replace(/(^|\s)#([\p{L}\p{N}][\p{L}\p{N}_-]*)/gu, (_, lead: string, tag: string) => {
      tags.push(tag.toLowerCase());
      return lead;
    })
    .replace(/\s{2,}/g, ' ')
    .trim();
  return { title, tags: [...new Set(tags)] };
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

type Row =
  | { kind: 'idea'; idea: Idea }
  | { kind: 'tag'; tag: Tag }
  | { kind: 'command'; command: (typeof COMMANDS)[number] }
  | { kind: 'save' };

export function QuickCapture({
  mode: initialMode,
  user,
  tags,
  initialTitle,
  initialBody,
  onClose,
  onCreated,
  onOpenIdea,
  onFilterTag,
  onGoTo,
}: {
  mode: QuickMode;
  user: User;
  tags: Tag[];
  initialTitle?: string;
  initialBody?: string;
  onClose: () => void;
  onCreated: () => void;
  onOpenIdea: (id: number) => void;
  onFilterTag: (tag: string) => void;
  onGoTo: (view: View) => void;
}) {
  const [mode, setMode] = useState<QuickMode>(initialMode);
  const [text, setText] = useState(initialTitle ?? '');
  const [body, setBody] = useState(initialBody ?? '');
  const [extraTags, setExtraTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState<string | null>(null);
  const [results, setResults] = useState<Idea[]>([]);
  const [similar, setSimilar] = useState<Idea[]>([]);
  const [active, setActive] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const isPro = user.plan === 'pro' || user.is_trialing;
  const { setTheme } = useTheme();
  const { scale, setScale, MIN_SCALE, MAX_SCALE } = useFontSize();
  const query = useDebounced(text.trim(), 220);

  useEffect(() => {
    inputRef.current?.focus();
  }, [mode]);

  // Search: ideas by meaning (Pro) or words (Free keeps its meaning-search quota).
  useEffect(() => {
    if (mode !== 'search' || query.length < 2 || query.startsWith('/')) {
      setResults([]);
      return;
    }
    let cancelled = false;
    api
      .searchIdeas(query, isPro ? 'semantic' : 'keyword', isPro ? undefined : { ftsMode: 'or' })
      .then((r) => { if (!cancelled) setResults(r.results.slice(0, 6)); })
      .catch(() => { if (!cancelled) setResults([]); });
    return () => { cancelled = true; };
  }, [mode, query, isPro]);

  // Capture: what's already in the library, shown before saving.
  useEffect(() => {
    const title = splitHashTags(query).title;
    if (mode !== 'capture' || title.length < 12) {
      setSimilar([]);
      return;
    }
    let cancelled = false;
    api
      .searchIdeas(title, isPro ? 'semantic' : 'keyword', isPro ? undefined : { ftsMode: 'or' })
      .then((r) => { if (!cancelled) setSimilar(r.results.slice(0, 3)); })
      .catch(() => { if (!cancelled) setSimilar([]); });
    return () => { cancelled = true; };
  }, [mode, query, isPro]);

  const rows: Row[] = useMemo(() => {
    if (mode !== 'search') return [];
    const q = text.trim().toLowerCase();
    if (q.startsWith('/')) {
      const w = q.slice(1);
      return COMMANDS.filter((c) => !w || c.words.includes(w) || c.label.toLowerCase().includes(w)).map((command) => ({ kind: 'command' as const, command }));
    }
    const out: Row[] = results.map((idea) => ({ kind: 'idea' as const, idea }));
    if (q.length >= 2) {
      tags.filter((t) => t.name.toLowerCase().includes(q.replace(/^#/, ''))).slice(0, 3).forEach((tag) => out.push({ kind: 'tag', tag }));
      out.push({ kind: 'save' });
    }
    return out;
  }, [mode, text, results, tags]);

  useEffect(() => setActive(0), [rows.length, mode]);

  const save = async (open: boolean, fromText = text) => {
    const { title, tags: hashTags } = splitHashTags(fromText);
    if (!title || saving) return;
    setSaving(true);
    setError(null);
    try {
      const allTags = [...new Set([...hashTags, ...extraTags])].slice(0, 20);
      const idea = await api.createIdea({
        title,
        body: body.trim() || undefined,
        tags: allTags.length > 0 ? allTags : undefined,
      });
      onCreated();
      onClose();
      if (open) onOpenIdea(idea.id);
    } catch (err) {
      if (isIdeaLimitError(err)) setLimitReached(true);
      else setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const choose = (row: Row | undefined) => {
    if (!row) return;
    if (row.kind === 'idea') { onClose(); onOpenIdea(row.idea.id); }
    else if (row.kind === 'tag') { onClose(); onFilterTag(row.tag.name); }
    else if (row.kind === 'command') {
      const id = row.command.id;
      if (VIEWS.has(id)) { onClose(); onGoTo(id as View); }
      else if (id === 'bigger') setScale(Math.min(MAX_SCALE, scale + 10));
      else if (id === 'smaller') setScale(Math.max(MIN_SCALE, scale - 10));
      else { setTheme(id as 'dark' | 'light' | 'system'); onClose(); }
    }
    else void save(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'Tab' && dialogRef.current) {
      // Keep focus inside the dialog.
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>('input, textarea, button, a[href]');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    }
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (mode === 'search') {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, rows.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
      else if (e.key === 'Enter' && e.shiftKey) { e.preventDefault(); void save(false); }
      else if (e.key === 'Enter') { e.preventDefault(); choose(rows[active]); }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      void save(e.metaKey || e.ctrlKey);
    }
  };

  const addDraftTag = () => {
    const name = (tagDraft ?? '').trim().replace(/^#/, '').toLowerCase();
    if (name && !extraTags.includes(name)) setExtraTags((t) => [...t, name]);
    setTagDraft(null);
    inputRef.current?.focus();
  };

  const parsed = splitHashTags(text);
  const shownTags = [...new Set([...parsed.tags, ...extraTags])];

  return (
    <div className="qc-scrim" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="qc"
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'search' ? 'Search' : 'New idea'}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <label className="qc-field">
          <Icon name={mode === 'search' ? 'search' : 'add'} size={18} className={mode === 'capture' ? 'qc-accent' : undefined} />
          <input
            ref={inputRef}
            className="qc-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onInputKey}
            placeholder={mode === 'search' ? 'Search ideas, tags, or type / for commands' : 'What’s the idea?'}
            aria-label="Idea, search or command"
            role={mode === 'search' ? 'combobox' : undefined}
            aria-expanded={mode === 'search' ? rows.length > 0 : undefined}
            aria-controls={mode === 'search' ? 'qc-results' : undefined}
            aria-activedescendant={mode === 'search' && rows.length > 0 ? `qc-row-${active}` : undefined}
          />
          <button type="button" className="qc-esc" onClick={onClose} aria-label="esc to close">esc</button>
        </label>

        {mode === 'capture' && (
          <>
            <div className="qc-details">
              <textarea
                className="qc-body"
                rows={2}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void save(true); } }}
                placeholder="Details (optional)"
                aria-label="Details (optional)"
              />
              <span className="qc-tags">
                {shownTags.map((t) => (
                  <button
                    key={t}
                    className="qc-tag"
                    onClick={() => {
                      setExtraTags((x) => x.filter((y) => y !== t));
                      if (parsed.tags.includes(t)) setText(text.replace(new RegExp(`(^|\\s)#${t}\\b`, 'i'), '$1').trim());
                    }}
                    aria-label={`Remove tag ${t}`}
                  >
                    {t}
                  </button>
                ))}
                {tagDraft === null ? (
                  <button className="qc-tag qc-tag-add" onClick={() => setTagDraft('')}>+ tag</button>
                ) : (
                  <input
                    className="qc-tag-input"
                    autoFocus
                    value={tagDraft}
                    onChange={(e) => setTagDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addDraftTag(); }
                      if (e.key === 'Escape') { e.stopPropagation(); setTagDraft(null); }
                    }}
                    onBlur={addDraftTag}
                    aria-label="New tag"
                    list="qc-tag-options"
                  />
                )}
                <datalist id="qc-tag-options">
                  {tags.slice(0, 200).map((t) => <option key={t.id} value={t.name} />)}
                </datalist>
                <span className="qc-note">· the rest are added for you after saving</span>
              </span>
            </div>

            <div className="qc-rows">
              {limitReached ? (
                <div className="qc-limit" role="status">
                  <span className="qc-limit-title">You have {user.active_idea_count} of 50 ideas on Free</span>
                  <span className="qc-limit-text">New ideas won’t save until you archive a few or go Pro. Nothing you have is deleted.</span>
                  <span className="qc-limit-actions">
                    <button className="qc-btn" onClick={() => { onClose(); onGoTo('ideas'); }}>Archive some</button>
                    <a className="qc-btn qc-btn-primary" href="/upgrade">Go Pro</a>
                  </span>
                </div>
              ) : (
                <>
                  <button className="qc-row qc-row-active" disabled={!parsed.title || saving} onClick={() => void save(false)}>
                    <Icon name="add" size={16} className="qc-accent" />
                    <span className="qc-row-main">{saving ? 'Saving…' : 'Save as a new idea'}</span>
                    <kbd className="qc-key">↵</kbd>
                  </button>
                  <button className="qc-row" disabled={!parsed.title || saving} onClick={() => void save(true)}>
                    <Icon name="external" size={16} />
                    <span className="qc-row-main">Save and open</span>
                    <kbd className="qc-key">⌘↵</kbd>
                  </button>
                  {similar.length > 0 && (
                    <>
                      <span className="qc-section">Already in your library · add to one instead?</span>
                      {similar.map((idea) => (
                        <button key={idea.id} className="qc-row" onClick={() => { onClose(); onOpenIdea(idea.id); }}>
                          <span className="qc-dot" style={{ background: `var(--nr-status-${idea.status})` }} aria-hidden="true" />
                          <span className="qc-row-main">
                            <span className="qc-row-title">{idea.title}</span>
                            <span className="qc-row-meta"><span className="qc-num">#{idea.number}</span> · {STATUS_LABEL[idea.status]}{idea.tags.length > 0 ? ` · ${idea.tags.slice(0, 2).join(', ')}` : ''}</span>
                          </span>
                          {idea.score != null && idea.score >= 0.75 && <span className="qc-match">Very similar</span>}
                        </button>
                      ))}
                    </>
                  )}
                  {error && <span className="qc-error" role="alert">{error}</span>}
                </>
              )}
            </div>
            <div className="qc-foot">
              <span>↵ save</span><span>⌘↵ save and open</span><span>tab details</span><span># tag</span>
              <span className="qc-grow" />
              <button className="qc-foot-link" onClick={() => setMode('search')}>Search instead</button>
            </div>
          </>
        )}

        {mode === 'search' && (
          <>
            <div className="qc-rows" id="qc-results" role="listbox" aria-label="Results">
              {rows.length === 0 && (
                <span className="qc-empty">{text.trim() ? 'Searching…' : 'Type to search your ideas by meaning and by words. / for commands.'}</span>
              )}
              {rows.map((row, i) => {
                const prev = rows[i - 1];
                const heading = !prev || prev.kind !== row.kind
                  ? row.kind === 'idea' ? 'Ideas' : row.kind === 'tag' ? 'Tags' : row.kind === 'command' ? 'Commands' : 'Capture'
                  : null;
                return (
                  <div key={i}>
                    {heading && <span className="qc-section">{heading}{row.kind === 'idea' && isPro ? <span className="qc-section-note"> · by meaning, not just words</span> : null}</span>}
                    <button
                      id={`qc-row-${i}`}
                      role="option"
                      aria-selected={i === active}
                      className={`qc-row${i === active ? ' qc-row-active' : ''}`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => choose(row)}
                    >
                      {row.kind === 'idea' && (
                        <>
                          <span className="qc-dot" style={{ background: `var(--nr-status-${row.idea.status})` }} aria-hidden="true" />
                          <span className="qc-row-main">
                            <span className="qc-row-title">{row.idea.title}</span>
                            <span className="qc-row-meta"><span className="qc-num">#{row.idea.number}</span> · {STATUS_LABEL[row.idea.status]}</span>
                          </span>
                        </>
                      )}
                      {row.kind === 'tag' && (
                        <>
                          <span className="qc-hash" aria-hidden="true">#</span>
                          <span className="qc-row-main">{row.tag.name}</span>
                          <span className="qc-row-meta">{row.tag.idea_count} ideas</span>
                        </>
                      )}
                      {row.kind === 'command' && (
                        <>
                          <Icon name={row.command.icon} size={16} />
                          <span className="qc-row-main">{row.command.label}</span>
                        </>
                      )}
                      {row.kind === 'save' && (
                        <>
                          <Icon name="add" size={16} className="qc-accent" />
                          <span className="qc-row-main">Save “{splitHashTags(text).title}” as a new idea</span>
                          <kbd className="qc-key">⇧↵</kbd>
                        </>
                      )}
                      {i === active && row.kind !== 'save' && <kbd className="qc-key">↵</kbd>}
                    </button>
                  </div>
                );
              })}
              {limitReached && <span className="qc-error" role="alert">You’re at the Free limit of 50 ideas. Archive a few or go Pro to save more.</span>}
              {error && <span className="qc-error" role="alert">{error}</span>}
            </div>
            <div className="qc-foot">
              <span>↑↓ move</span><span>↵ open</span><span>⇧↵ save as idea</span>
              <span className="qc-grow" />
              <button className="qc-foot-link" onClick={() => setMode('capture')}>New idea instead</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
